import React, { useState } from "react";
import IconCheck from "../../../../../common-deprecated/svg/toyota/check.svg";
import IconCircle from "../../../../../common-deprecated/svg/toyota/circle.svg";
import IconInfo from "../../../../../common-deprecated/svg/toyota/info.svg";
import IconMinus from "../../../../../common-deprecated/svg/toyota/minus.svg";
import IconAdd from "../../../../../../assets/toyota/svg/icon-add.svg";
import type { EquipmentSpecNameIdValueType } from "../../../../../common-deprecated/features/compare/utils/newCarCompareTypes";
import * as Button from "../../../../../common-deprecated/styles/toyota/Button";
import ColourSpecValuePopup from "../../common/highlight/content/ColourSpecValuePopup";
import { useCompareV2Selector } from "../../../redux/store";
import { consumptionCertificationMap } from "../../../../../common-deprecated/constants";
import type { CompareV2LabelType } from "../../../utils/labels";
import { useCompareV2OptionalLabel, useCompareV2Label, CompareV2Label } from "../../../utils/labels";
import { getWLTPLabelKey } from "../../../utils/utils";
import IconWrapper from "../../../../../common-deprecated/styles/toyota/IconWrapper";
import { SrOnly } from "../../../../../common-deprecated/styles/v2/toyota/utilities/ScreenreaderOnly";
import Popover from "../../../../../common-deprecated/components/popover/aem/Popover";

type CompareEquipmentOrSpecValuePropsType = {
    value: EquipmentSpecNameIdValueType;
    carColour?: string;
};

const EquipmentOrSpecValue = (props: CompareEquipmentOrSpecValuePropsType): JSX.Element => {
    const cars = useCompareV2Selector(({ newCarCompare }) => newCarCompare.grades);
    const { value, carColour } = props;
    const [showPopup, setShowPopup] = useState<boolean>(false);
    const [standardLabel, optionalLabel, notApplicableLabel, optionalPackLabel] = useCompareV2Label([
        "compareBodyLegendStandard",
        "compareBodyLegendOptional",
        "compareBodyLegendNotApplicable",
        "compareBodyLegendOptionalPack",
    ]);

    const wltpLabel = useCompareV2OptionalLabel(
        (value.type
            ? getWLTPLabelKey(consumptionCertificationMap[value.type], value.isElectric)
            : "") as CompareV2LabelType,
    );

    switch (value.value) {
        case "s":
            // This equipment item is standard, but can be not applicable for certain colours, we check that first:
            if (carColour && value.colours && value.colours.length && !value.colours.includes(carColour)) {
                return (
                    <>
                        <IconWrapper iconColor="iconNeutral">
                            <IconMinus aria-hidden="true" />
                        </IconWrapper>
                        <Button.Link
                            p={0}
                            onClick={() => {
                                setShowPopup(!showPopup);
                            }}
                        >
                            <IconWrapper iconColor="iconNeutral">
                                <IconInfo aria-hidden="true" />
                            </IconWrapper>
                            <SrOnly>
                                <CompareV2Label label="ariaMoreInfo" />
                            </SrOnly>
                        </Button.Link>
                        {showPopup && (
                            <ColourSpecValuePopup
                                content={
                                    <>
                                        {value.colours.map((c) => {
                                            const colourName = cars.map(
                                                (grade) =>
                                                    grade.availableColours?.find((color) => color.code === c)?.name,
                                            );

                                            return (
                                                <p key={c}>
                                                    {colourName}
                                                    <IconWrapper iconColor="success">
                                                        <IconCheck aria-hidden="true" />
                                                    </IconWrapper>
                                                </p>
                                            );
                                        })}
                                    </>
                                }
                                closePopup={() => {
                                    setShowPopup(false);
                                }}
                            />
                        )}
                    </>
                );
            }
            return (
                <IconWrapper iconColor="success">
                    <IconCheck aria-hidden="true" />
                    <SrOnly>{standardLabel}</SrOnly>
                </IconWrapper>
            );
        case "o":
            // This equipment item is optional, but can be not applicable for certain colours, we check that first:
            if (carColour && value.colours && value.colours.length && !value.colours.includes(carColour)) {
                return (
                    <>
                        <IconWrapper iconColor="iconNeutral">
                            <IconMinus aria-hidden="true" />
                        </IconWrapper>
                        <Button.Link
                            p={0}
                            onClick={() => {
                                setShowPopup(!showPopup);
                            }}
                        >
                            <IconWrapper iconColor="iconNeutral">
                                <IconInfo aria-hidden="true" />
                            </IconWrapper>
                            <SrOnly>
                                <CompareV2Label label="ariaMoreInfo" />
                            </SrOnly>
                        </Button.Link>
                        {showPopup && (
                            <ColourSpecValuePopup
                                content={
                                    <>
                                        {value.colours.map((c) => {
                                            const colourName = cars.map(
                                                (grade) =>
                                                    grade.availableColours?.find((color) => color.code === c)?.name,
                                            );

                                            return (
                                                <p key={c}>
                                                    {colourName}
                                                    <IconWrapper iconColor="info">
                                                        <IconCircle aria-hidden="true" />
                                                    </IconWrapper>
                                                </p>
                                            );
                                        })}
                                    </>
                                }
                                closePopup={() => {
                                    setShowPopup(false);
                                }}
                            />
                        )}
                    </>
                );
            }
            return (
                <IconWrapper iconColor="info">
                    <IconCircle aria-hidden="true" />
                    <SrOnly>{optionalLabel}</SrOnly>
                </IconWrapper>
            );
        case "op":
            return (
                <IconWrapper>
                    <IconAdd aria-hidden="true" />
                    <SrOnly>{optionalPackLabel}</SrOnly>
                </IconWrapper>
            );
        case "":
            return (
                <IconWrapper iconColor="iconNeutral">
                    <IconMinus aria-hidden="true" />
                    <SrOnly>{notApplicableLabel}</SrOnly>
                </IconWrapper>
            );
        default:
            return (
                <>
                    {value.value}
                    {value.type && wltpLabel && <Popover showOnClick content={wltpLabel} useHTML />}
                </>
            );
    }
};

export default EquipmentOrSpecValue;
